import { useState, useEffect, useRef } from 'react';
import { sendChat, getChatMessages } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, X, Send } from 'lucide-react';

export default function ChatWidget() {
  const { user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && user) {
      loadMessages();
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [open, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    try {
      const res = await getChatMessages();
      setMessages(res.data.messages || []);
    } catch {}
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      await sendChat({ message: input.trim() });
      setInput('');
      await loadMessages();
    } catch {}
    finally { setSending(false); }
  };

  if (!user) return null;

  return (
    <>
      <button onClick={() => setOpen(!open)} className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-110 transition-transform" data-testid="chat-widget-trigger">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-80 sm:w-96 bg-white border border-border rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] overflow-hidden" data-testid="chat-widget">
          <div className="bg-primary text-primary-foreground p-4">
            <h3 className="font-display font-bold">{t('liveChat')}</h3>
            <p className="text-xs opacity-80">{t('liveChatDesc')}</p>
          </div>
          <div className="h-64 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 && <p className="text-center text-muted-foreground text-sm py-8">{t('chatWelcome')}</p>}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.is_admin ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.is_admin ? 'bg-accent text-foreground' : 'bg-primary text-primary-foreground'}`}>
                  {m.is_admin && <p className="text-[10px] font-semibold mb-0.5 opacity-70">Support</p>}
                  {m.message}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-border flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSend()} placeholder={t('typeMessage')} className="rounded-xl text-sm" />
            <Button size="icon" onClick={handleSend} disabled={sending} className="bg-primary text-primary-foreground rounded-xl shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
